import { infiniteQueryOptions } from '@tanstack/lit-query';
import { buildURL, fetchRequest } from 'Utils/api-fetch-request';

const VIDEO_COMMENTS_BASE_URL = '/api/videos/';

// Must match `comment/services.py::DEFAULT_COMMENTS_PAGE_SIZE`. Offsets are
// computed on the client (`pageParam + COMMENTS_PAGE_SIZE`); if the two drift,
// pages silently overlap or gap without any error.
export const COMMENTS_PAGE_SIZE = 20;

export interface CommentAuthor {
    first_name: string;
    last_name: string;
    avatar: string | null;
    url: string;
    id?: number;
}

export interface CommentAttachment {
    url: string;
    mimetype: string;
}

export interface CommentData {
    id: number;
    comment: string;
    when: string;
    author: CommentAuthor;
    reply_url: string;
    pm_url: string;
    thumbs_up_count: number;
    user_has_thumbed_up: boolean;
    is_public?: boolean;
    children?: CommentData[];
    attachments?: CommentAttachment[];
}

export interface CommentsApiResponse {
    count: number;
    total_count: number;
    results: CommentData[];
}

interface FetchCommentsPageParams {
    videoSlug: string;
    limit: number;
    offset: number;
}

export interface PostCommentParams {
    videoSlug: string;
    comment: string;
    attachmentIds?: number[];
    /** Immediate parent comment id — the id of the comment the user clicked
     * "Reply" on, not the top-level ancestor. Backend stores an MPTT tree so
     * the reply chain is preserved (notifications route to `parent.author`)
     * even though the read side flattens all replies of a tree into the root's
     * `children[]`. Serialised on the wire as `parent`. */
    parentId?: number;
}

interface UploadCommentAttachmentParams {
    videoSlug: string;
    file: File;
    /** Optional AbortSignal so the composer can cancel an in-flight upload
     * when the user removes the chip before the request settles. */
    signal?: AbortSignal;
}

interface CommentThumbupParams {
    videoSlug: string;
    commentId: number;
}

export async function fetchCommentsPage({
    videoSlug,
    limit,
    offset,
}: FetchCommentsPageParams): Promise<CommentsApiResponse> {
    return fetchRequest(buildURL(`${VIDEO_COMMENTS_BASE_URL}${videoSlug}/comments/`, { limit, offset }));
}

export async function uploadCommentAttachment({
    videoSlug,
    file,
    signal,
}: UploadCommentAttachmentParams): Promise<{ id: number }> {
    const formData = new FormData();
    formData.append('upload', file);
    return fetchRequest(`${VIDEO_COMMENTS_BASE_URL}${videoSlug}/comments/upload/`, {
        method: 'POST',
        body: formData,
        signal,
    });
}

export async function postComment({
    videoSlug,
    comment,
    attachmentIds,
    parentId,
}: PostCommentParams): Promise<CommentData> {
    const formData = new FormData();
    formData.append('comment', comment);
    attachmentIds?.forEach((id) => formData.append('attachment_ids', String(id)));
    if (parentId !== undefined) formData.append('parent', String(parentId));
    return fetchRequest(`${VIDEO_COMMENTS_BASE_URL}${videoSlug}/comments/`, {
        method: 'POST',
        body: formData,
    });
}

export async function thumbsupComment({ videoSlug, commentId }: CommentThumbupParams): Promise<void> {
    await fetchRequest(`${VIDEO_COMMENTS_BASE_URL}${videoSlug}/comments/${commentId}/thumbup/`, { method: 'POST' });
}

export async function removeThumbsupComment({ videoSlug, commentId }: CommentThumbupParams): Promise<void> {
    await fetchRequest(`${VIDEO_COMMENTS_BASE_URL}${videoSlug}/comments/${commentId}/thumbup/`, { method: 'DELETE' });
}

/**
 * Shared infinite-query options for a video's comments list. `infiniteQueryOptions()`
 * brands the queryKey and threads the `pageParam` type through so `setQueryData(...)`
 * on the submit path is typed to `InfiniteData<CommentsApiResponse, number>`.
 */
export function commentsQueryOptions(videoSlug: string) {
    return infiniteQueryOptions({
        queryKey: ['videoComments', videoSlug] as const,
        queryFn: ({ pageParam }) => fetchCommentsPage({ videoSlug, limit: COMMENTS_PAGE_SIZE, offset: pageParam }),
        initialPageParam: 0,
        getNextPageParam: (lastPage, _allPages, lastPageParam) => {
            const nextOffset = lastPageParam + COMMENTS_PAGE_SIZE;
            return nextOffset < lastPage.count ? nextOffset : undefined;
        },
    });
}
